import { useEffect, useState } from "react";
import { Lightbulb, Plus, Loader2 } from "lucide-react";
import { apiFetch } from "@/lib/api";
import { PageTitle, PageSubtitle, Loader, EmptyState, Card } from "@/components/common";
import { cn } from "@/lib/utils";

const CATEGORIES = { idea: "Idea", problema: "Problema", miglioramento: "Miglioramento", altro: "Altro" };
const STATUSES = { nuovo: "Nuovo", in_valutazione: "In valutazione", pianificato: "Pianificato", risolto: "Risolto", archiviato: "Archiviato" };
function dt(v) {
  return v ? new Date(v).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
}

export default function FeedbackPage() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ category: "idea", title: "", message: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    apiFetch("/feedback").then(setTickets).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.message.trim() || form.message.trim().length < 3) {
      setError("Scrivi un messaggio di almeno 3 caratteri.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await apiFetch("/feedback", { method: "POST", body: JSON.stringify({ category: form.category, title: form.title.trim(), message: form.message.trim() }) });
      setForm({ category: "idea", title: "", message: "" });
      setShowForm(false);
      load();
    } catch (e) {
      setError(e.message || "Riprova.");
    } finally {
      setSaving(false);
    }
  };

  const chip = (active) => `rounded-full px-3.5 py-2 text-xs font-bold ${active ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"}`;

  return (
    <div className="space-y-3">
      <div className="flex items-start justify-between">
        <div>
          <PageTitle>Aiutaci a migliorare CargoFlow</PageTitle>
          <PageSubtitle>Idee, problemi e proposte: raccontaci come rendere l'app migliore.</PageSubtitle>
        </div>
        <button data-testid="feedback-add-button" onClick={() => setShowForm((v) => !v)} className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary text-primary-foreground">
          <Plus className={cn("h-6 w-6 transition-transform", showForm && "rotate-45")} />
        </button>
      </div>

      {showForm ? (
        <Card>
          <p className="mb-1 text-lg font-extrabold text-foreground">Nuova segnalazione</p>
          <label className="mt-2 block text-xs font-bold text-card-foreground">Categoria</label>
          <div className="mt-1 flex flex-wrap gap-2">
            {Object.entries(CATEGORIES).map(([k, l]) => (
              <button key={k} data-testid={`feedback-category-${k}`} onClick={() => setForm({ ...form, category: k })} className={chip(form.category === k)}>{l}</button>
            ))}
          </div>
          <label className="mt-2 block text-xs font-bold text-card-foreground">Titolo (facoltativo)</label>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Riassumi in poche parole" className="min-h-[48px] w-full rounded-xl border border-border bg-card px-3 text-[15px] outline-none focus:border-primary" />
          <label className="mt-2 block text-xs font-bold text-card-foreground">Messaggio *</label>
          <textarea data-testid="feedback-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Descrivi la tua idea o il problema..." className="min-h-[90px] w-full rounded-xl border border-border bg-card px-3 py-2 text-[15px] outline-none focus:border-primary" />
          {error ? <p className="mt-2 text-center text-sm text-destructive">{error}</p> : null}
          <button data-testid="feedback-submit" onClick={save} disabled={saving} className="mt-3 flex min-h-[50px] w-full items-center justify-center gap-2 rounded-xl bg-primary text-[13px] font-extrabold text-primary-foreground disabled:opacity-60">
            {saving ? <Loader2 className="h-5 w-5 animate-spin" /> : null} INVIA SEGNALAZIONE
          </button>
        </Card>
      ) : null}

      {loading ? (
        <Loader />
      ) : tickets.length === 0 ? (
        <EmptyState icon={Lightbulb} title="Nessuna segnalazione" text="Invia la tua prima idea o segnalazione con il pulsante in alto." />
      ) : (
        tickets.map((t) => (
          <Card key={t.id} data-testid={`feedback-ticket-${t.id}`} className="space-y-2">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <p className="truncate font-extrabold text-foreground">{t.title || CATEGORIES[t.category]}</p>
                <p className="text-xs text-muted-foreground">{CATEGORIES[t.category]} · {dt(t.updated_at)}</p>
              </div>
              <span className="rounded-full bg-secondary/60 px-2.5 py-1 text-[11px] font-extrabold text-secondary-foreground">{STATUSES[t.status] || t.status}</span>
            </div>
            {t.last_message ? <p className="text-[13px] text-card-foreground">{t.last_from_admin ? "CargoFlow: " : ""}{t.last_message}</p> : null}
          </Card>
        ))
      )}
    </div>
  );
}
